import { View, Text, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMemo } from 'react';
import { useRouter } from 'expo-router';
import Unicon from '@/components/General/Unicon';
import ContentLoader from '@/components/General/ContentLoader';
import Chip from '@/components/General/Chip';
import Avatar from '@/components/People/Avatar';
import FeatureIcon from '@/components/General/FeatureIcon';
import CutCard from '@/components/General/CutCard';
import dashboardStyles from './dashboardStyles';
import { Query } from '@/utils/react-query/Queries';
import { parseTimeFrame } from '@/utils/dater';

const MAX_ROWS = 10;

/**
 * ActiveCareSection — the main-Dashboard "Active care" feed (§25.9).
 * Independently permission-gated ('View Follow Up' at the section filter;
 * follow_up_list_care_episodes is team-only server-side) and bounded —
 * protected care details never enter the broad dashboard payload.
 * Cards show person + brief context + connector/Needs owner + recent
 * metadata only; medical/logistics text stays on the episode route.
 */
const ActiveCareSection = () => {
    const router = useRouter();
    const { data: loggedInUser } = Query.people.loggedInUser();

    const { data, isLoading } = Query.followUp.careEpisodes({
        organizationID: loggedInUser?.organizationID,
        status: 'ACTIVE',
        limit: MAX_ROWS,
        options: { enabled: !!loggedInUser?.organizationID && !!loggedInUser?.can?.('View Follow Up') },
    });

    const episodes = useMemo(
        () => (data?.pages || []).flatMap((page) => page?.rows || []).slice(0, MAX_ROWS),
        [data]
    );

    const header = (
        <View style={[styles.header, dashboardStyles.header]}>
            <View style={[styles.titleRow, dashboardStyles.titleRow]}>
                <FeatureIcon feature="paths" />
                <Text style={[styles.title, dashboardStyles.title]}>Active Care</Text>
            </View>
            <Pressable hitSlop={20} onPress={() => router.push('/follow-up')} style={styles.linkBtn}>
                <Text style={styles.linkText}>View All</Text>
                <Unicon name="chevron-forward-outline" size={12} colorVariant="primary" />
            </Pressable>
        </View>
    );

    if (isLoading) {
        return (
            <CutCard testID="dashboard-section-activeCare" style={[styles.card, dashboardStyles.card]} contentStyle={dashboardStyles.content}>
                {header}
                <View style={styles.loadingWrap}>
                    <ContentLoader containerHeight={120} />
                </View>
            </CutCard>
        );
    }

    if (episodes.length === 0) {
        return (
            <CutCard testID="dashboard-section-activeCare" style={[styles.card, dashboardStyles.card]} contentStyle={dashboardStyles.content}>
                {header}
                <View style={styles.emptyWrap}>
                    <Text style={styles.emptyText}>No active care right now.</Text>
                </View>
            </CutCard>
        );
    }

    return (
        <CutCard testID="dashboard-section-activeCare" style={[styles.card, dashboardStyles.card]} contentStyle={dashboardStyles.content}>
            {header}
            <View style={styles.listWrap}>
                {episodes.map((item) => (
                    <Pressable
                        key={item.careEpisodeID}
                        testID={`dashboard-care-${item.careEpisodeID}`}
                        accessibilityRole="button"
                        onPress={() => router.push(`/follow-up/care/${item.careEpisodeID}`)}
                        style={styles.row}
                    >
                        <Avatar
                            size={34}
                            profile={{
                                id: item.personID,
                                firstName: item.firstName,
                                lastName: item.lastName,
                                avatarStorageKey: item.avatarStorageKey,
                                avatarUpdatedAt: item.avatarUpdatedAt,
                            }}
                        />
                        <View style={styles.rowBody}>
                            <Text style={styles.rowTitle} numberOfLines={1}>{item.personName || 'Unknown person'}</Text>
                            <Text style={styles.rowMeta} numberOfLines={1}>
                                {[
                                    item.context || 'Care',
                                    item.lastTouchAt ? `Last activity ${parseTimeFrame(item.lastTouchAt, true)}` : 'No visits logged yet',
                                ].filter(Boolean).join(' · ')}
                            </Text>
                        </View>
                        {!item.primaryCaregiverPersonID && (
                            <Chip icon="person-add-outline" colorVariant="warning">Needs Owner</Chip>
                        )}
                        <Unicon name="chevron-forward-outline" size={14} colorVariant="textTertiary" />
                    </Pressable>
                ))}
            </View>
        </CutCard>
    );
};

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    card: {
        width: '100%',
        backgroundColor: colors.surface,
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: spacing.md,
        paddingTop: spacing.md,
        paddingBottom: spacing.sm,
    },
    titleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs + 2 },
    title: {
        fontSize: typography.caption,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
    },
    linkBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 2,
        paddingVertical: spacing.xs,
        paddingHorizontal: spacing.xs + 2,
        borderRadius: 6,
        _web: { _hover: { backgroundColor: colors.hover } },
    },
    linkText: { color: colors.primary, fontSize: typography.caption, fontFamily: fonts.heading },
    loadingWrap: { paddingHorizontal: spacing.md, paddingBottom: spacing.md },
    emptyWrap: { paddingHorizontal: spacing.md, paddingBottom: spacing.md },
    emptyText: { fontSize: typography.caption, color: colors.textSecondary, fontStyle: 'italic' },
    listWrap: { paddingHorizontal: spacing.sm, paddingBottom: spacing.sm, gap: 4 },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        paddingVertical: 8,
        paddingHorizontal: spacing.sm,
        borderRadius: 10,
        _web: { cursor: 'pointer', _hover: { backgroundColor: colors.hover } },
    },
    rowBody: { flex: 1, minWidth: 0 },
    rowTitle: { fontSize: 14, fontFamily: fonts.bold, color: colors.text },
    rowMeta: { fontSize: 12, color: colors.textSecondary, marginTop: 1 },
}));

export default ActiveCareSection;
